import { useCallback, useEffect, useRef, useState } from 'react';

// FILA DE PRESENTES EM VÍDEO (Champions, Liga, …)
//
// Regras, as mesmas em todos os ecrãs:
//   • um presente de cada vez no ecrã; os outros esperam a sua vez;
//   • quando o atual acaba (fim, erro ou tempo limite), entra o seguinte;
//   • a fila tem teto — numa rajada de combos não se mostram 20 um a um;
//   • o mesmo presente enviado duas vezes toca duas vezes (cada entrada tem
//     uma chave própria, para o React recomeçar o vídeo);
//   • ao sair do ecrã, esvazia-se tudo e nada toca depois.
//
// O motor é puro (sem React) para poder ser testado em Node; o hook em baixo
// é a casca fina que o liga ao estado do ecrã.

export const TETO_FILA = 5;

export function criarFilaDePresentes({ teto = TETO_FILA, aoComecar = null, aoMudar = null } = {}) {
  let atual = null;
  let fila = [];
  let contador = 0;
  let viva = true;

  const avisar = () => { if (typeof aoMudar === 'function') aoMudar(atual, fila.length); };

  const comecar = (entrada) => {
    atual = entrada;
    avisar();
    if (typeof aoComecar === 'function') {
      try { aoComecar(entrada); } catch (erro) { console.warn('[fila] aoComecar falhou:', erro); }
    }
  };

  return {
    // Mete um presente na fila; se o ecrã está livre, começa já.
    enfileirar(url) {
      if (!viva || !url) return false;
      const entrada = { url, chave: `${++contador}` };
      if (!atual) { comecar(entrada); return true; }
      fila = [...fila, entrada].slice(-teto);
      avisar();
      return true;
    },
    // O presente atual acabou (por fim, erro ou tempo limite): entra o seguinte.
    terminar(chave) {
      if (!viva || !atual) return;
      // Um "ended" atrasado de um vídeo que já saiu do ecrã não pode fechar o seguinte.
      if (chave !== undefined && chave !== atual.chave) return;
      const proximo = fila.shift() || null;
      if (proximo) comecar(proximo);
      else { atual = null; avisar(); }
    },
    // Partida nova / revanche: esvazia tudo, mas a fila continua a servir.
    limpar() {
      if (!viva) return;
      atual = null;
      fila = [];
      avisar();
    },
    // Sair do ecrã: esvazia e desliga para sempre.
    destruir() {
      viva = false;
      atual = null;
      fila = [];
      avisar();
    },
    get atual() { return atual; },
    get emEspera() { return fila.length; },
    get viva() { return viva; },
  };
}

// Hook: devolve { atual, enfileirar, terminar }. `atual` é { url, chave } ou null.
// `aoComecar` corre uma vez por presente (a bomba); é lido por ref, por isso
// o ecrã pode passar uma função nova em cada render sem reiniciar a fila.
export function useFilaDePresentes({ aoComecar, teto = TETO_FILA } = {}) {
  const [atual, setAtual] = useState(null);
  const refAoComecar = useRef(aoComecar);
  const refFila = useRef(null);

  useEffect(() => { refAoComecar.current = aoComecar; }, [aoComecar]);

  // Criada à primeira utilização (e outra vez depois de um desmontar: o React
  // em desenvolvimento monta, desmonta e volta a montar o mesmo ecrã).
  const obterFila = useCallback(() => {
    if (!refFila.current || !refFila.current.viva) {
      refFila.current = criarFilaDePresentes({
        teto,
        aoComecar: (entrada) => refAoComecar.current?.(entrada),
        aoMudar: (novoAtual) => setAtual(novoAtual),
      });
    }
    return refFila.current;
  }, [teto]);

  useEffect(() => {
    const fila = obterFila();
    return () => { fila.destruir(); };
  }, [obterFila]);

  const enfileirar = useCallback((url) => obterFila().enfileirar(url), [obterFila]);
  const terminar = useCallback((chave) => refFila.current?.terminar(chave), []);
  const limpar = useCallback(() => refFila.current?.limpar(), []);

  return { atual, enfileirar, terminar, limpar };
}
